import React, { useEffect, useState, createContext } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@tabler/icons-react";
import { motion } from "framer-motion";
import "./AppleCarousel.css";

export const CarouselContext = createContext({
  onCardClose: () => {},
  currentIndex: 0,
});

export const AppleCarousel = ({ children, initialScroll = 0 }) => {
  const carouselRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const items = React.Children.toArray(children);

  const checkScrollability = React.useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const canScroll = scrollWidth > clientWidth + 10; // Add 10px buffer
      setCanScrollLeft(scrollLeft > 5); // 5px threshold for better UX
      setCanScrollRight(canScroll && scrollLeft < scrollWidth - clientWidth - 5); // 5px threshold
      // Calculate current page for indicator
      const itemsPerPage = 3;
      const cardWidth = window.innerWidth < 768 ? 176 : 320; // 160+16 or 300+20
      const page = Math.floor(scrollLeft / (cardWidth * itemsPerPage)) + 1;
      setCurrentPage(Math.min(Math.max(1, page), Math.ceil(items.length / itemsPerPage)));
    } else {
      // If ref not ready, set defaults
      setCanScrollLeft(false);
      setCanScrollRight(items.length > 3);
    }
  }, [items.length]);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      // Delay check to ensure DOM is ready
      setTimeout(checkScrollability, 100);
    }
  }, [initialScroll, checkScrollability]);

  useEffect(() => {
    // Check scrollability after items are rendered - multiple attempts to ensure it works
    const timeouts = [
      setTimeout(() => checkScrollability(), 100),
      setTimeout(() => checkScrollability(), 300),
      setTimeout(() => checkScrollability(), 500),
    ];
    
    const handleResize = () => {
      setTimeout(checkScrollability, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      window.removeEventListener('resize', handleResize);
    };
  }, [items.length, checkScrollability]);


  const scrollLeft = () => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.clientWidth;
      const scrollAmount = containerWidth * 0.8; // Scroll by 80% of visible width
      carouselRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.clientWidth;
      const scrollAmount = containerWidth * 0.8; // Scroll by 80% of visible width
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleCardClose = (index) => {
    if (carouselRef.current) {
      const cardWidth = window.innerWidth < 768 ? 160 : 300;
      const gap = window.innerWidth < 768 ? 16 : 20;
      const scrollPosition = (cardWidth + gap) * index;
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  // Always show carousel with buttons if there are items
  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose, currentIndex }}>
      <div className="apple-carousel-container">
        <div
          className="apple-carousel-scroll"
          ref={carouselRef}
          onScroll={checkScrollability}
        >
          <div className="apple-carousel-gradient-right"></div>
          <div className="apple-carousel-items">
            {items.map((item, index) => (
              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    delay: 0.1 * index,
                    ease: "easeOut",
                    once: true,
                  },
                }}
                key={`card-${index}`}
                className="apple-carousel-item"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
        {/* Always show controls, even if disabled */}
        <div className="apple-carousel-controls" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
          <button
            className="apple-carousel-button"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            aria-label="Previous items"
            title="Previous"
            style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
          >
            <IconArrowNarrowLeft className="apple-carousel-icon" style={{ display: 'block' }} />
          </button>
          <span className="apple-carousel-indicator" style={{ display: 'inline-block', visibility: 'visible' }}>
            {currentPage} / {Math.ceil(items.length / 3)}
          </span>
          <button
            className="apple-carousel-button"
            onClick={scrollRight}
            disabled={!canScrollRight}
            aria-label="Next items"
            title="Next"
            style={{ display: 'flex', visibility: 'visible', opacity: 1 }}
          >
            <IconArrowNarrowRight className="apple-carousel-icon" style={{ display: 'block' }} />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

export default AppleCarousel;

